// Copyright (c) 2026 Or2 LLC. All rights reserved.
// Insight Platform - proprietary software by Nnaico (https://nnaico.com).
// See the LICENSE file in this repository. All rights reserved.

// Appearance = three independent user choices, stored on the user profile:
//   theme         'light' | 'dark'
//   color_scheme  an id from the skin file (public/themes.js)
//   ui_style      'normal' | 'terminal' (typography/decorations, never colors)
//
// Every color comes from window.INSIGHT_THEMES (public/themes.js, loaded like
// config.js). This module only resolves it: scheme → inherits base → missing
// tokens derived from the accent. Changes fan out through the `themeChanged`
// window event, which every page already listens to.

import { useEffect, useState } from 'react'

const LEGACY_TERMINAL_KEY = 'cme_terminal'

// Minimal fallback so the app still renders if themes.js is missing
const FALLBACK_SKIN = {
  defaults: {},
  base: {},
  schemes: { default: { label: 'Default', light: { accent: '#2563eb' }, dark: { accent: '#3b82f6' } } },
}

export const getSkin = () => {
  const skin = window.INSIGHT_THEMES
  return skin && skin.schemes && Object.keys(skin.schemes).length ? skin : FALLBACK_SKIN
}

export const withAlpha = (color, alpha) => {
  if (!color?.startsWith('#')) return color
  const hex = color.length === 4 ? color.replace(/^#(.)(.)(.)$/, '#$1$1$2$2$3$3') : color
  const n = parseInt(hex.slice(1, 7), 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
}

const readUser = () => {
  try {
    return JSON.parse(localStorage.getItem('cme_user') || '{}')
  } catch {
    return {}
  }
}

const legacyTerminal = () => {
  try {
    return localStorage.getItem(LEGACY_TERMINAL_KEY) === '1'
  } catch {
    return false
  }
}

// The current choices, with defaults filled in
export const getAppearance = () => {
  const user = readUser()
  const skin = getSkin()
  const mode = user.theme === 'dark' ? 'dark' : 'light'
  const style = user.ui_style || (legacyTerminal() ? 'terminal' : 'normal')
  const fallback = skin.defaults?.[style]?.[mode]
  const scheme = [user.color_scheme, fallback].find((id) => id && skin.schemes[id])
    || Object.keys(skin.schemes)[0]
  return { mode, style, scheme }
}

// Full token set for a scheme in a mode: base → derived from accent → scheme
export const resolvePalette = (schemeId, mode) => {
  const skin = getSkin()
  const scheme = skin.schemes[schemeId] || Object.values(skin.schemes)[0]
  const own = scheme[mode] || {}
  const accent = own.accent || skin.base?.[mode]?.accent || '#2563eb'
  const dark = mode === 'dark'
  const accentText = own.accentText || accent
  const solid = own.solidAccent ?? skin.base?.[mode]?.solidAccent
  const neutralWash = dark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)'
  // Translucent variant of a color. With solidAccent (colors that turn muddy
  // when diluted over the background) lines/borders stay the solid color and
  // background washes become neutral.
  const tint = (color, alpha) => {
    if (!solid) return withAlpha(color, alpha)
    return alpha >= 0.25 ? color : neutralWash
  }
  const derived = {
    accent,
    accentText,
    strongText: accentText,
    userBubbleBg: tint(accent, dark ? 0.12 : 0.08),
    userBubbleBorder: tint(accent, dark ? 0.25 : 0.3),
    selectedBg: tint(accent, 0.16),
    hoverBg: tint(accent, 0.08),
  }
  return { ...derived, ...skin.base?.[mode], ...own, tint }
}

// CSS custom properties for the plain-CSS parts (App.css, nnaico.css)
const cssVars = (p) => {
  const deco = p.decoration || p.accent
  return {
    '--app-accent': p.accent,
    '--app-accent-wash': p.tint(p.accent, 0.1),
    '--app-deco': deco,
    '--app-deco-muted': p.tint(deco, 0.35),
    '--app-deco-strong': p.tint(deco, 0.6),
    '--app-deco-wash': p.tint(deco, 0.1),
    '--app-scrollbar': p.scrollbar || p.tint(deco, 0.35),
    '--app-scrollbar-hover': p.scrollbar || p.tint(deco, 0.6),
    '--app-accent-text': p.accentText,
    '--app-strong-text': p.strongText,
    '--app-selection-bg': p.accent,
    '--app-selection-ink': p.selectionInk,
    '--app-separator': p.divider || p.tint(p.accent, 0.25),
    '--app-user-bubble-bg': p.userBubbleBg,
    '--app-user-bubble-border': p.userBubbleBorder,
  }
}

// Sync the <html> classes and CSS variables with the stored choices. Safe to
// call as often as needed — every change path funnels here.
export const applyDocumentThemeClasses = () => {
  const { mode, style, scheme } = getAppearance()
  const root = document.documentElement
  root.classList.toggle('dark', mode === 'dark')
  root.classList.toggle('terminal', style === 'terminal')
  for (const [name, value] of Object.entries(cssVars(resolvePalette(scheme, mode)))) {
    root.style.setProperty(name, value)
  }
}

// Change one or more choices: { theme, color_scheme, ui_style }. Applies
// immediately, notifies every page, then saves to the profile in background.
export const setAppearance = async (changes) => {
  localStorage.setItem('cme_user', JSON.stringify({ ...readUser(), ...changes }))
  applyDocumentThemeClasses()
  window.dispatchEvent(new Event('themeChanged'))

  const apiUrl = window.INSIGHT_CONFIG?.API_URL ?? ''
  try {
    await fetch(`${apiUrl}/users/me`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('cme_token')}`,
      },
      body: JSON.stringify(changes),
    })
  } catch (err) {
    console.error('Failed to save appearance:', err)
  }
}

// React hook: the current choices, re-read on every themeChanged event
export const useAppearance = () => {
  const [appearance, setState] = useState(getAppearance)
  useEffect(() => {
    const sync = () => {
      applyDocumentThemeClasses()
      setState(getAppearance())
    }
    window.addEventListener('themeChanged', sync)
    return () => window.removeEventListener('themeChanged', sync)
  }, [])
  return appearance
}
